/**
 * sn-geschenk — Geschenk-Automatik fuer Horizon (26.09.2026). Markup: snippets/sn-geschenk.liquid
 *
 * Ab 49 € Warenwert (ohne Geschenk) liegt das gewaehlte Geschenk gratis im Korb, darunter fliegt es raus.
 * Auswahl-Wechsel tauscht die Geschenkzeile. Wer das Geschenk per Muelleimer entfernt, bekommt es in
 * dieser Sitzung nicht automatisch zurueck (bis der Warenwert wieder unter 49 € faellt) — „+ Gratis
 * dazulegen“ holt es jederzeit zurueck. Gleiche Regeln wie die Dawn-Fassung (sn-cart-goal).
 *
 * Aktualisierung: Wir aendern den Korb per AJAX-API und melden das Horizon ueber dasselbe
 * Ereignis, das auch der Kaufen-Knopf benutzt (shopify:cart:lines-update). Horizon rendert
 * Einschub, Warenkorbseite und Zaehler dann selbst neu. action 'update' statt 'add', damit sich
 * der Einschub dabei nicht von selbst oeffnet.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFTS = [39859707805872, 39859707936944, 46870515974475, 46870470295883, 39859719766192, 39859713441968];
const THRESHOLD = 4900;
const OFF_KEY = 'snGiftOff';

let busy = false;

const getOff = () => {
  try { return sessionStorage.getItem(OFF_KEY) === '1'; } catch { return false; }
};
const setOff = (on) => {
  try { on ? sessionStorage.setItem(OFF_KEY, '1') : sessionStorage.removeItem(OFF_KEY); } catch {}
};

const isGift = (item) => Boolean(item && item.properties && item.properties._sn_gift);

/** Alle Warenkorb-Abschnitte auf der Seite (Einschub + ggf. Warenkorbseite). */
function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function eventTarget() {
  return document.querySelector('theme-drawer#cart-drawer') || document;
}

async function getCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' });
  return res.json();
}

/**
 * Fuehrt eine Korb-Aenderung aus und laesst Horizon neu rendern.
 * @param {'add'|'change'|'update'} kind
 * @param {object} body
 * @param {Array<{merchandiseId: string, quantity: number}>} lines
 */
async function mutate(kind, body, lines) {
  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  eventTarget().dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise })
  );
  try {
    const url = `${window.Shopify?.routes?.root || '/'}cart/${kind}.js`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sections.length ? { ...body, sections: sections.join(','), sections_url: location.pathname } : body),
    });
    const data = await res.json();
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: cart.items, source: 'sn-geschenk', didError: !res.ok },
    });
    return cart;
  } catch (error) {
    deferred.resolve({ detail: { didError: true, source: 'sn-geschenk' } });
    throw error;
  }
}

function selectedGift() {
  const sel = /** @type {HTMLSelectElement | null} */ (document.querySelector('[data-sn-wahl-select]'));
  const id = sel ? Number(sel.value) : GIFTS[0];
  return GIFTS.includes(id) ? id : GIFTS[0];
}

const addGift = (id) =>
  mutate('add', { items: [{ id, quantity: 1, properties: { _sn_gift: '1' } }] }, [
    { merchandiseId: String(id), quantity: 1 },
  ]);

const removeLine = (line) =>
  mutate('change', { id: line.key, quantity: 0 }, [{ merchandiseId: String(line.variant_id), quantity: 0 }]);

/**
 * Sparpaket im Korb (Ken 28.09.2026): Wer die Einzeldose im Korb auf 3 oder mehr hochzaehlt, bekam bisher
 * keinen Paketrabatt. Wir tauschen solche Zeilen in die Paket-Varianten (5er zuerst, dann 3er, Rest einzeln),
 * z. B. 3 → 1 x 3er, 4 → 1 x 3er + 1, 6 → 1 x 5er + 1, 8 → 1 x 5er + 1 x 3er.
 * Pakete erkennen wir am Variantentitel („1 Dose“, „3 Dosen“, „5 Dosen“) wie die Paketwahl (sn-kaufboxen).
 * @returns {Promise<boolean>} true, wenn der Korb geaendert wurde
 */
const mengeAusTitel = (t) => parseInt(String(t || '').trim(), 10) || 0;
async function sparpakete(cart) {
  let geaendert = false;
  for (const item of cart.items || []) {
    if (isGift(item) || item.selling_plan_allocation || item.quantity < 3) continue;
    if (mengeAusTitel(item.variant_title) !== 1) continue;
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${item.handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) continue;
    const produkt = await res.json();
    const pakete = (produkt.variants || [])
      .map((v) => ({ id: v.id, n: mengeAusTitel(v.title), da: v.available }))
      .filter((v) => v.n > 1 && v.da)
      .sort((a, b) => b.n - a.n);
    let rest = item.quantity;
    const dazu = [];
    for (const p of pakete) {
      const anzahl = Math.floor(rest / p.n);
      if (anzahl > 0) { dazu.push({ id: p.id, quantity: anzahl }); rest -= anzahl * p.n; }
    }
    if (!dazu.length) continue;
    // EIN Aufruf (update.js legt fehlende Varianten neu an) — zwei Aufrufe liessen den Einschub kurz leer aufblitzen
    const updates = { [item.key]: rest };
    for (const d of dazu) {
      const vorhanden = (cart.items || []).find((i) => i.variant_id === d.id && !isGift(i) && !i.selling_plan_allocation);
      if (vorhanden) updates[vorhanden.key] = vorhanden.quantity + d.quantity;
      else updates[d.id] = d.quantity;
    }
    await mutate('update', { updates }, [
      { merchandiseId: String(item.variant_id), quantity: rest },
      ...dazu.map((d) => ({ merchandiseId: String(d.id), quantity: d.quantity })),
    ]);
    geaendert = true;
  }
  return geaendert;
}

async function check() {
  if (busy) return;
  busy = true;
  try {
    let cart = await getCart();
    if (await sparpakete(cart)) cart = await getCart();
    let qual = 0;
    let giftLine = null;
    for (const item of cart.items || []) {
      if (isGift(item)) giftLine = item;
      else qual += item.final_line_price;
    }
    if (qual < THRESHOLD && getOff()) setOff(false);

    if (qual >= THRESHOLD && !giftLine && !getOff()) {
      await addGift(selectedGift());
    } else if (qual < THRESHOLD && giftLine) {
      await removeLine(giftLine);
    }
  } catch {
    // Netzfehler: beim naechsten Korb-Ereignis erneut versuchen
  } finally {
    busy = false;
  }
}

// Jede Korb-Aenderung (Kaufen-Knopf, Menge, Entfernen, Kaufleiste) -> danach pruefen.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const e = /** @type {any} */ (event);
  const done = e.promise && typeof e.promise.then === 'function' ? e.promise : Promise.resolve();
  done
    .then((result) => {
      if (result?.detail?.source === 'sn-geschenk') return;
      setTimeout(check, 120);
    })
    .catch(() => setTimeout(check, 400));
});

// Auswahl gewechselt: Bild sofort tauschen, Geschenkzeile ersetzen.
document.addEventListener('change', async (event) => {
  const sel = event.target;
  if (!(sel instanceof HTMLSelectElement) || !sel.matches('[data-sn-wahl-select]')) return;
  const id = Number(sel.value);
  const box = sel.closest('[data-sn-wahl]');
  const img = box?.querySelector('[data-sn-wahl-bild]');
  const src = box?.getAttribute(`data-img-${id}`);
  if (img instanceof HTMLImageElement && src) img.src = src;
  if (busy) return;
  busy = true;
  try {
    const cart = await getCart();
    const giftLine = (cart.items || []).find(isGift);
    if (giftLine && giftLine.variant_id !== id) {
      await mutate('change', { id: giftLine.key, quantity: 0 }, [
        { merchandiseId: String(giftLine.variant_id), quantity: 0 },
      ]);
      await addGift(id);
    }
  } catch {
  } finally {
    busy = false;
  }
});

// „+ Gratis dazulegen“ und Muelleimer an der Geschenkzeile
document.addEventListener(
  'click',
  (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    if (t.closest('[data-sn-wahl-dazu]')) {
      setOff(false);
      if (!busy) {
        busy = true;
        addGift(selectedGift()).catch(() => {}).finally(() => { busy = false; });
      }
      return;
    }
    if (t.closest('.sn-geschenk-zeile .cart-items__remove')) setOff(true);
  },
  true
);

// Beim Laden einmal pruefen (z. B. Korb aus einer frueheren Sitzung).
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => check());
else check();
